import { useCallback, useEffect, useRef } from "react";

import { useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  BILLING_VALID_TABS,
  type BillingSubSearch,
  type BillingTab,
  UnifiedBillingView,
  validateBillingSubSearch,
} from "@/components/billing/unified-billing-view";
import { showToast } from "@/lib/ui/toast";

interface BillingSearch extends BillingSubSearch {
  tab: BillingTab;
  /** Stripe Checkout success round-trip (`?success=1&session_id=...`). */
  success?: boolean;
  session_id?: string;
  /** Wallet-covered checkout (`?success=1&wallet=1`) — no Stripe session. */
  wallet?: boolean;
}

export const Route = createFileRoute("/_authenticated/account/user/billing")({
  validateSearch: (search: Record<string, unknown>): BillingSearch => {
    const raw = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: BILLING_VALID_TABS.includes(raw as BillingTab) ? (raw as BillingTab) : "subscription",
      success: search.success === true || search.success === "1" || search.success === 1,
      session_id: typeof search.session_id === "string" ? search.session_id : undefined,
      wallet: search.wallet === true || search.wallet === "1" || search.wallet === 1,
      ...validateBillingSubSearch(search),
    };
  },
  component: UserBillingPage,
});

function UserBillingPage() {
  const { tab, success, session_id } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useLingui();
  const handledSuccess = useRef(false);

  // Stripe Checkout lands back here with ?success=1&session_id=... — webhooks
  // confirm the subscription in prod; in dev they can't reach localhost, so we
  // call the idempotent /api/checkout/confirm fallback before refetching.
  useEffect(() => {
    if (!success || handledSuccess.current) return;
    handledSuccess.current = true;
    showToast(t`Payment confirmed — your subscription is active.`, "success");
    const refresh = () =>
      queryClient.invalidateQueries({ queryKey: ["api", "/api/account/billing"] });
    if (import.meta.env.DEV && session_id) {
      apiFetch
        .post("/api/checkout/confirm", { sessionId: session_id })
        .catch(() => undefined)
        .finally(refresh);
    } else {
      void refresh();
    }
    navigate({
      to: "/account/user/billing",
      search: { tab },
      replace: true,
    });
  }, [success, session_id, tab, navigate, queryClient, t]);

  const setTab = useCallback(
    (next: BillingTab) => navigate({ to: "/account/user/billing", search: { tab: next } }),
    [navigate],
  );
  return (
    <>
      <UnifiedBillingView activeTab={tab} onTabChange={setTab} />
      <AccountSecurityBanner />
    </>
  );
}
